import { useState, ChangeEvent, useEffect, useRef } from "react";
import { Tooltip } from "react-tooltip";
import { useReactToPrint } from "react-to-print";
import { Field, Label, Switch, Description, Input, Button } from "@headlessui/react";
import {
	CheckIcon,
	MinusIcon,
	PencilIcon,
	PlusIcon,
	PrinterIcon,
	XMarkIcon,
} from "@heroicons/react/24/solid";
import { handleEnterKey } from "../../app/keyboardUtils";
import CoursePromptReviewGroups from "../peerReviewGroups/coursePromptReviewGroups/CoursePromptReviewGroups";
import { CoursePromptsGroupInfo } from "../peerReviewGroups/coursePromptReviewGroups/coursePromptReviewGroupsApiSlice";
import PrintPeerReviewDocument from "../peerReviews/PrintPeerReviewDocument";
import { PeerReviewSettingsViewModel } from "./promptsApiSlice";

interface PeerReviewStepProps {
	promptName: string;
	model: PeerReviewSettingsViewModel;
	genreId: number;
	setPeerReviewModel: (model?: PeerReviewSettingsViewModel) => void;
}

export default function PeerReviewStep(props: PeerReviewStepProps) {
	const { model, genreId, setPeerReviewModel } = props;

	const contentRef = useRef<HTMLDivElement>(null);
	const printPeerReview = useReactToPrint({ contentRef });

	const requiredQuestions = model.generalReviewQuestions.filter((q) => q.genreId == genreId);
	const [reviewQuestions, setReviewQuestions] = useState(model.reviewQuestions);

	const [peerReviewEnabled, setPeerReviewEnabled] = useState(model.allowReviews);
	const [anonymousAuthor, setAnonymousAuthor] = useState(model.anonymousAuthor);
	const [anonymousReviewers, setAnonymousReviewer] = useState(model.anonymousReviewers);
	const [sentenceStartersEnabled, setSentenceStartersEnabled] = useState(
		model.allowSentenceStarters
	);
	const [groups, setGroups] = useState(model.groups.coursePromptGroups);
	const [customQuestions, setCustomQuestions] = useState(model.customReviewQuestions);
	const [newQuestion, setNewQuestion] = useState("");
	const [editQuestion, setEditQuestion] = useState("");
	const [editCustomIndex, setEditCustomIndex] = useState<number>();

	const [disableCheckboxes, setDisableCheckboxes] = useState(false);
	const [showAllQuestions, setShowAllQuestions] = useState(false);

	function handleCheckboxChange(e: ChangeEvent<HTMLInputElement>) {
		const updatedQuestions = reviewQuestions.map((question) =>
			question.id === Number(e.target.value)
				? { ...question, isEnabled: !question.isEnabled }
				: question
		);
		setReviewQuestions(updatedQuestions);
	}

	function handleCustomCheckboxChange(questionIndex: number) {
		const updatedQuestions = customQuestions.map((question, index) =>
			questionIndex === index ? { ...question, isEnabled: !question.isEnabled } : question
		);
		setCustomQuestions(updatedQuestions);
	}

	function handleAddCustomQuestion() {
		setCustomQuestions([
			...customQuestions,
			{ question: newQuestion, isEnabled: !disableCheckboxes },
		]);
		setNewQuestion("");
	}

	function handleRemoveCustomQuestion(index: number) {
		const updatedQuestions = [...customQuestions];
		updatedQuestions.splice(index, 1);
		setCustomQuestions(updatedQuestions);
	}

	function handleEditCustomQuestion(questionIndex: number) {
		setEditCustomIndex(questionIndex);
		setEditQuestion(customQuestions[questionIndex].question);
	}

	function handleSaveEditCustomQuestion() {
		const updatedQuestions = customQuestions.map((question, index) =>
			editCustomIndex === index ? { ...question, question: editQuestion } : question
		);
		setCustomQuestions(updatedQuestions);
		setEditQuestion("");
		setEditCustomIndex(undefined);
	}

	useEffect(() => {
		const reviewEnabledCount = reviewQuestions.filter(
			(q) => q.isEnabled && q.genreId == genreId
		).length;
		const customEnabledCount = customQuestions.filter((q) => q.isEnabled).length;
		setDisableCheckboxes(reviewEnabledCount + customEnabledCount >= 6);
	}, [customQuestions, reviewQuestions]);

	useEffect(() => {
		setPeerReviewModel(updateModel());
	}, [
		peerReviewEnabled,
		anonymousAuthor,
		anonymousReviewers,
		sentenceStartersEnabled,
		reviewQuestions,
		customQuestions,
		groups,
	]);

	function updateGroups(updatedGroups: CoursePromptsGroupInfo) {
		setGroups(updatedGroups);
	}

	function updateModel(): PeerReviewSettingsViewModel | undefined {
		if (!model) return undefined;
		const reviewModel: PeerReviewSettingsViewModel = {
			...model,
			allowReviews: peerReviewEnabled,
			anonymousAuthor,
			anonymousReviewers,
			allowSentenceStarters: sentenceStartersEnabled,
			reviewQuestions,
			customReviewQuestions: customQuestions,
			groups: {
				...model.groups,
				coursePromptGroups: groups,
			},
		};
		return reviewModel;
	}

	return (
		<section className="space-y-6">
			<div
				className={`${peerReviewEnabled && genreId != 0 ? "border-b border-gray-900/10" : ""} flex pb-4`}>
				<div className="w-full md:w-1/4 pr-3">
					<h2 className="leading-7">Peer Review</h2>
					<p className="text-sm mt-2 leading-6 text-gray-600 pr-3">
						NOTE: In order for a student to request peer reviews, they must be a member
						of a peer review group. Groups can be created in the Reviews tab.
					</p>
				</div>
				{genreId === 0 ? (
					<span className="leading-6">
						A genre must be selected before peer review options are available. Peer
						review questions are tailored based on genre.
					</span>
				) : (
					<div className="flex flex-col w-full md:w-3/4 gap-y-4">
						<Field className="flex items-center justify-between">
							<Label as="span" className="block font-medium leading-6">
								Enable Peer Review
							</Label>
							<Switch
								checked={peerReviewEnabled}
								onChange={setPeerReviewEnabled}
								className="group relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600">
								<span
									aria-hidden="true"
									className="pointer-events-none inline-block size-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
								/>
							</Switch>
						</Field>
						{peerReviewEnabled && (
							<div className="flex flex-col w-full gap-y-4">
								<Field className="flex items-center justify-between">
									<span className="flex flex-col">
										<Label as="span" className="block font-medium leading-6">
											Anonymous Author
										</Label>
										<Description
											as="span"
											className="text-sm leading-6 text-gray-600">
											Hide the identity of the essay writer from the reviewer.
										</Description>
									</span>
									<Switch
										checked={anonymousAuthor}
										onChange={setAnonymousAuthor}
										className="group relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600">
										<span
											aria-hidden="true"
											className="pointer-events-none inline-block size-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
										/>
									</Switch>
								</Field>
								<Field className="flex items-center justify-between">
									<span className="flex flex-col">
										<Label as="span" className="block font-medium leading-6">
											Anonymous Reviewer
										</Label>
										<Description
											as="span"
											className="text-sm leading-6 text-gray-600">
											Hide the identity of the reviewer from the essay writer.
										</Description>
									</span>
									<Switch
										checked={anonymousReviewers}
										onChange={setAnonymousReviewer}
										className="group relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600">
										<span
											aria-hidden="true"
											className="pointer-events-none inline-block size-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
										/>
									</Switch>
								</Field>
								<Field className="flex items-center justify-between">
									<Label as="span" className="block font-medium leading-6">
										Sentence Starters
									</Label>
									<Switch
										checked={sentenceStartersEnabled}
										onChange={setSentenceStartersEnabled}
										className="group relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600">
										<span
											aria-hidden="true"
											className="pointer-events-none inline-block size-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
										/>
									</Switch>
								</Field>
							</div>
						)}
					</div>
				)}
			</div>
			{peerReviewEnabled && genreId != 0 && (
				<section className="space-y-6">
					<div className="flex pb-4 border-b border-gray-900/10">
						<div className="w-full md:w-1/4 pr-3">
							<h2 className="leading-7">Peer Review Groups</h2>
							<p className="text-sm mt-2 leading-6 text-gray-600 pr-3">
								NOTE: Once a student requests a peer review, the prompt peer review
								groups will lock and will not be able to be changed.
							</p>
							<p className="text-sm mt-2 leading-6 text-gray-600 pr-3">
								NOTE: If you do not specify groups here, the default course groups
								will be used.
							</p>
						</div>
						<div className="flex flex-col w-full md:w-3/4 gap-y-4">
							<CoursePromptReviewGroups
								groupsData={model.groups}
								updateGroups={updateGroups}
							/>
						</div>
					</div>
					<div className="flex pb-4">
						<div className="w-full md:w-1/4 pr-3">
							<h2 className="leading-7 flex flex-row items-center">
								<span className="mr-6">Peer Review Questions</span>
								<PrinterIcon
									className="inline min-w-5 size-5 mr-3 text-gray-500 hover:cursor-pointer hover:text-gray-700"
									data-tooltip-id="print-peer-review"
									onClick={printPeerReview}
								/>
								<Tooltip id="print-peer-review" place="top" className="!text-sm">
									Print a peer review worksheet
								</Tooltip>
							</h2>
							<p className="mt-1 text-sm leading-6 text-gray-600">
								Choose up to six other questions, in addition to the required
								questions.
							</p>
							<div ref={contentRef} className="hidden print:block">
								<PrintPeerReviewDocument
									title={props.promptName}
									generalQuestions={requiredQuestions.filter((q) => q.isEnabled)}
									reviewQuestions={reviewQuestions.filter((q) => q.isEnabled)}
									customQuestions={customQuestions.filter((q) => q.isEnabled)}
								/>
							</div>
						</div>
						<div className="flex flex-col w-full md:w-3/4 gap-y-4">
							<Field>
								<Label as="span" className="block font-medium leading-6">
									Required Questions
								</Label>
								<table className="tbl tbl-data mt-2">
									<tbody>
										{requiredQuestions.map((question) => {
											return (
												<tr key={"question-" + question.id}>
													<td className="!w-12">
														<input
															value={question.id}
															checked={question.isEnabled}
															type="checkbox"
															disabled={true}
															className="size-4 rounded-sm border-gray-300 text-primary-700 focus:ring-primary-700 disabled:hover:cursor-not-allowed"
														/>
													</td>
													<td>{question.question}</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</Field>
							{reviewQuestions.filter((q) => q.genreId == genreId).length > 0 && (
								<Field>
									<Label as="span" className="block font-medium leading-6">
										Additional Questions
									</Label>
									<table className="tbl tbl-data mt-2">
										<tbody>
											{reviewQuestions
												.filter((q) => q.genreId == genreId)
												.slice(0, showAllQuestions ? undefined : 5)
												.map((question) => {
													return (
														<tr key={"question-" + question.id}>
															<td className="!w-12">
																<input
																	value={question.id}
																	checked={question.isEnabled}
																	disabled={
																		question.isEnabled
																			? false
																			: disableCheckboxes
																	}
																	type="checkbox"
																	className="size-4 rounded-sm border-gray-300 text-primary-700 focus:ring-primary-700 disabled:cursor-not-allowed"
																	onChange={(e) => {
																		handleCheckboxChange(e);
																	}}
																/>
															</td>
															<td>{question.question}</td>
															<td></td>
														</tr>
													);
												})}
										</tbody>
									</table>
									{reviewQuestions.filter((q) => q.genreId == genreId).length >
										5 && (
										<div className="mt-2.5">
											<Button
												type="button"
												onClick={() =>
													setShowAllQuestions(!showAllQuestions)
												}
												className="text-primary-600 hover:text-primary-700 font-semibold">
												{showAllQuestions ? (
													<span className="relative flex items-center">
														<MinusIcon className="h-5 inline mr-0" />
														&nbsp;View Less
													</span>
												) : (
													<span className="relative flex items-center ">
														<PlusIcon className="h-5 inline mr-0 " />
														&nbsp;View All Additional Questions
													</span>
												)}
											</Button>
										</div>
									)}
								</Field>
							)}
							<Field>
								<Label as="span" className="block font-medium leading-6">
									Custom Questions
								</Label>
								<table className="tbl tbl-data mt-2">
									<tbody>
										{customQuestions.map((question, index) => {
											return (
												<tr key={"custom-question-" + index}>
													<td className="!w-12">
														<input
															value={index}
															checked={question.isEnabled}
															disabled={
																question.isEnabled
																	? false
																	: disableCheckboxes
															}
															type="checkbox"
															className="size-4 rounded-sm border-gray-300 text-primary-700 focus:ring-primary-700 disabled:cursor-not-allowed"
															onChange={() => {
																handleCustomCheckboxChange(index);
															}}
														/>
													</td>
													<td>
														{editCustomIndex == index ? (
															<div className="flex grow items-stretch rounded-md shadow-sm">
																<Input
																	type="text"
																	name="editQuestion"
																	id="editQuestion"
																	className="block w-full rounded-none rounded-l-md border-0 py-1.5 px-3 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600"
																	value={editQuestion}
																	onChange={(e) =>
																		setEditQuestion(
																			e.target.value
																		)
																	}
																	onKeyDown={(e) =>
																		handleEnterKey(
																			e,
																			handleSaveEditCustomQuestion
																		)
																	}></Input>
																<Button
																	type="button"
																	disabled={!editQuestion}
																	onClick={
																		handleSaveEditCustomQuestion
																	}
																	className="relative inline-flex items-center rounded-r-md px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed">
																	<span className="flex items-center">
																		<CheckIcon className="size-4 mr-2" />{" "}
																		Save
																	</span>
																</Button>
															</div>
														) : (
															question.question
														)}
													</td>
													<td className="!text-end">
														<div className="flex justify-end items-center gap-x-2">
															{editCustomIndex === undefined && (
																<button
																	id={"edit-custom-" + index}
																	onClick={() =>
																		handleEditCustomQuestion(
																			index
																		)
																	}>
																	<PencilIcon className="size-4" />
																</button>
															)}
															<button
																id={"remove-custom-" + index}
																onClick={() =>
																	handleRemoveCustomQuestion(
																		index
																	)
																}>
																<XMarkIcon className="size-6" />
															</button>
														</div>
													</td>
												</tr>
											);
										})}
									</tbody>
								</table>
								<div className="flex mt-2 h-12 rounded-md shadow-sm">
									<Input
										type="text"
										name="newQuestion"
										id="newQuestion"
										className="w-full rounded-none rounded-l-md border-0 py-1.5 px-3 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600"
										placeholder="Add a custom peer review question..."
										value={newQuestion}
										onChange={(e) => setNewQuestion(e.target.value)}
										onKeyDown={(e) =>
											handleEnterKey(e, handleAddCustomQuestion)
										}></Input>
									<Button
										type="button"
										disabled={!newQuestion}
										onClick={handleAddCustomQuestion}
										className="relative inline-flex w-44 items-center rounded-r-md px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed">
										<span className="flex items-center">
											<PlusIcon className="size-4 mr-2" /> Add Question
										</span>
									</Button>
								</div>
							</Field>
						</div>
					</div>
				</section>
			)}
		</section>
	);
}
